import { Component, OnInit, AfterViewInit, Input, ChangeDetectorRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { PageEvent } from '@angular/material/paginator';
import { Customer } from '../../../interfaces/customer.interface';
import { InsuranceCompany, CompanyEmail, CompanyPhone, RelatedCustomer } from '../../../interfaces/insurance-company.interface';
import { ActivatedRoute } from '@angular/router';
import { CallCenterChipComponent } from '../../../shared/components/call-center-chip/call-center-chip.component';
import { SmallInputMenuComponent } from '../../../shared/components/small-input-menu/small-input-menu.component';
import { SmallIconMenuComponent, SmallIconMenuAction } from '../../../shared/components/small-icon-menu/small-icon-menu.component';
import { CircleIconButtonComponent } from '../../../shared/components/circle-icon-button/circle-icon-button.component';
import { PhoneFormatPipe } from '../../../shared/pipes';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { NewCustomerDialogComponent } from '../new-customer-dialog/new-customer-dialog.component';
import { NewCustomerDialogResult, NewCustomerDialogData } from '../../../interfaces/new-customer-dialog.interface';
import { CustomerListItem } from '../../../interfaces/customer-service.interface';
import { ContractsListTableComponent } from '../../contracts/contracts-list-table/contracts-list-table.component';
import { Contract } from '../../../interfaces/contract.interface';
import { ExtendedLogsTableComponent } from '../../admin/extended-logs/extended-logs-table/extended-logs-table.component';
import { AdminInteraction } from '../../../services/interactions.service';
import { CustomerDetailsUtilsService } from '../../../services/customer-details-utils.service';
import { CustomerDetailsManagementService } from '../../../services/customer-details-management.service';
import { ContactManagementService } from '../../../services/contact-management.service';
import { ChipMenuActionsService, ChipMenuState } from '../../../services/chip-menu-actions.service';
import { CustomerDataLoadingService } from '../../../services/customer-data-loading.service';
import { CustomerDataResult, CompanyDataResult } from '../../../interfaces/customer-service.interface';

@Component({
  selector: 'app-customers-details',
  imports: [
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatDividerModule,
    MatTabsModule,
    MatExpansionModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatSnackBarModule,
    FormsModule,
    CallCenterChipComponent,
    SmallInputMenuComponent,
    SmallIconMenuComponent,
    CircleIconButtonComponent,
    ContractsListTableComponent,
    ExtendedLogsTableComponent
],
  templateUrl: './customers-details.component.html',
  styleUrl: './customers-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomersDetailsComponent implements OnInit, AfterViewInit {
  @Input() customerId?: string;
  @Input() companyId?: string;
  @ViewChild('smallEmailInputMenuComponent') smallEmailInputMenuComponent!: SmallInputMenuComponent;
  @ViewChild('smallPhoneInputMenuComponent') smallPhoneInputMenuComponent!: SmallInputMenuComponent;
  @ViewChild('editInputMenuForChip') editInputMenuComponent!: SmallInputMenuComponent;
  @ViewChild('editTrigger') editTrigger!: MatMenuTrigger;
  
  // Menu triggers for small input menus
  @ViewChild('addEmailMenuTrigger') addEmailMenuTrigger!: MatMenuTrigger;
  @ViewChild('addPhoneMenuTrigger') addPhoneMenuTrigger!: MatMenuTrigger;
  @ViewChild('addCustomerPhoneMenuTrigger') addCustomerPhoneMenuTrigger!: MatMenuTrigger;
  @ViewChild('addCustomerEmailMenuTrigger') addCustomerEmailMenuTrigger!: MatMenuTrigger;

  // Core data
  customer: Customer | null = null;
  company: InsuranceCompany | null = null;
  individualCustomer: RelatedCustomer | null = null;
  contracts: Contract[] = [];
  interactions: AdminInteraction[] = [];

  // Loading states
  loading = true;
  contractsLoading = false;
  interactionsLoading = false;

  // Error states
  error: string | null = null;
  contractsError: string | null = null;
  interactionsError: string | null = null;

  // Chip menu state - managed by ChipMenuActionsService
  chipMenuState: ChipMenuState;

  // Customer selection for adding contacts
  selectedCustomerForPhone: any = null;

  constructor(
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private utilsService: CustomerDetailsUtilsService,
    private managementService: CustomerDetailsManagementService,
    private contactService: ContactManagementService,
    private chipMenuService: ChipMenuActionsService,
    private dataLoadingService: CustomerDataLoadingService
  ) {
    this.chipMenuState = this.chipMenuService.createInitialState();
  }

  ngAfterViewInit(): void {
    // Ensure all ViewChild references are initialized
    // Use setTimeout to prevent ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  ngOnInit(): void {
    // Use setTimeout to prevent ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      // Check if IDs are passed as inputs
      if (this.customerId) {
        this.loadCustomerData(this.customerId);
      } else if (this.companyId) {
        this.loadCompanyData(this.companyId);
      } else {
        // Check route parameters
        this.route.params.subscribe(params => {
          if (params['customerId']) {
            this.loadCustomerData(params['customerId']);
          } else if (params['companyId']) {
            this.loadCompanyData(params['companyId']);
          } else {
            this.error = 'Nincs megadott ügyfél vagy biztosító azonosító';
            this.loading = false;
            this.cdr.detectChanges();
          }
        });
      }
    }, 0);
  }

  private loadCustomerData(customerId: string): void {
    this.loading = true;
    this.error = null;
    this.resetDataObjects();

    this.dataLoadingService.loadCompleteCustomerData(parseInt(customerId)).subscribe({
      next: (result) => {
        if (result.state.error) {
          this.error = result.state.error;
        } else if (result.data) {
          this.customer = result.data.customer || null;
          this.individualCustomer = result.data.individualCustomer || null;
          this.contracts = result.data.contracts || [];
          this.interactions = result.data.interactions || [];
          
          // Load contracts and interactions separately with proper timing
          if (this.individualCustomer?.id) {
            setTimeout(() => {
              this.loadContractsForEntity('customer', this.individualCustomer!.id);
              this.loadInteractionsForCustomer(this.individualCustomer!.id);
            }, 0);
          }
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading customer:', err);
        this.error = 'Hiba történt az ügyfél adatok betöltése során';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadCompanyData(companyId: string): void {
    this.loading = true;
    this.error = null;
    this.resetDataObjects();

    this.dataLoadingService.loadCompleteCompanyData(parseInt(companyId)).subscribe({
      next: (result) => {
        if (result.state.error) {
          this.error = result.state.error;
        } else if (result.data?.company) {
          this.company = result.data.company;
          this.contracts = result.data.contracts || [];
          this.interactions = result.data.interactions || [];
          
          // Load contracts and interactions separately with proper timing
          setTimeout(() => {
            if (this.company?.id) {
              this.loadContractsForEntity('company', this.company.id);
              this.loadInteractionsForCompany(this.company.id);
            }
          }, 0);
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading company:', err);
        this.error = 'Hiba történt a cég adatok betöltése során';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private resetDataObjects(): void {
    this.company = null;
    this.customer = null;
    this.individualCustomer = null;
    this.contracts = [];
    this.interactions = [];
  }

  getStatusColor(status: string): string {
    return this.utilsService.getStatusColor(status);
  }

  formatCurrency(amount: number, currency: string = 'HUF'): string {
    return this.utilsService.formatCurrency(amount, currency);
  }

  formatDate(date: string | Date): string {
    if (!date) return '-';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('hu-HU');
  }

  onContactCustomer(): void {
    if (this.customer?.telefonszam) {
      window.open(`tel:${this.customer.telefonszam}`);
    }
  }

  onEmailCustomer(): void {
    if (this.customer?.email) {
      window.open(`mailto:${this.customer.email}`);
    }
  }

  onAddNewEmail(): void {
    // The small-input-menu will open automatically via matMenuTriggerFor
  }

  openEmailMenu(): void {
    // The menu will open via matMenuTriggerFor binding
  }

  onEmailAdded(email: string): void {
    if (!email || !email.trim()) {
      return;
    }

    // Get the current company ID
    const companyId = this.company?.id;
    if (!companyId) {
      console.error('Nem található cég ID az email hozzáadásához');
      this.managementService.showError('Hiba: Cég ID nem található');
      return;
    }

    // Use contact management service
    this.contactService.addCompanyEmail(companyId, email).subscribe({
      next: (response) => {
        if (response.success) {
          this.managementService.showSuccess(`Email cím "${email}" sikeresen hozzáadva!`);
          this.loadCompanyData(companyId.toString());
        } else {
          this.managementService.showError('Hiba az email hozzáadása során');
        }
      },
      error: (error) => {
        console.error('Error adding email:', error);
        this.managementService.showError(error.error?.detail || 'Hiba az email hozzáadása során');
      }
    });
  }

  onPhoneAdded(phone: string): void {
    if (!phone || !phone.trim()) {
      return;
    }

    // Get the current company ID
    const companyId = this.company?.id;
    if (!companyId) {
      console.error('Nem található cég ID a telefonszám hozzáadásához');
      this.managementService.showError('Hiba: Cég ID nem található');
      return;
    }

    // Use contact management service
    this.contactService.addCompanyPhone(companyId, phone).subscribe({
      next: (response) => {
        if (response.success) {
          this.managementService.showSuccess(`Telefonszám "${phone}" sikeresen hozzáadva!`);
          this.loadCompanyData(companyId.toString());
        } else {
          this.managementService.showError('Hiba a telefonszám hozzáadása során');
        }
      },
      error: (error) => {
        console.error('Error adding phone:', error);
        this.managementService.showError(error.error?.detail || 'Hiba a telefonszám hozzáadása során');
      }
    });
  }



  onEditCustomer(customer: any): void {
    
    // Használjuk a company ID-t, amihez a customer tartozik
    const companyId = this.company?.id;
    
    
    const dialogRef = this.dialog.open(NewCustomerDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'crm-dialog',
      data: {
        editMode: true,
        customerType: 'individual', // Megadjuk hogy individual típusú
        individualData: {
          id: customer.id,
          fullName: customer.fullName,
          address: customer.address || '',
          birthDate: customer.birthDate ? new Date(customer.birthDate) : null,
          taxNumber: customer.taxNumber || '',
          birthPlace: customer.birthPlace || '',
          motherName: customer.motherName || '',
          isExistingCustomer: customer.existingCustomer || false,
          companyId: companyId
        }
      } as NewCustomerDialogData
    });

    dialogRef.afterClosed().subscribe((result: NewCustomerDialogResult | undefined) => {
      const expandedCustomerId = customer.id;
      
      // Mindig biztosítjuk, hogy a panel nyitva maradjon (mind save, mind cancel esetén)
      setTimeout(() => {
        customer.expanded = true;
        this.cdr.detectChanges();
      }, 0);
      
      if (result?.action === 'save' && result.data?.individual) {
        this.managementService.showSuccess('Ügyfél adatok sikeresen frissítve!');
        
        // Újratöltjük az adatokat, de megőrizzük a nyitott állapotot
        if (this.company?.id) {
          this.loadCompanyData(this.company.id.toString());
          
          // Miután az adatok betöltődnek, nyitva tartjuk a szerkesztett ügyfél panel-jét
          setTimeout(() => {
            if (this.company?.relatedCustomers) {
              const editedCustomer = this.company.relatedCustomers.find(c => c.id === expandedCustomerId);
              if (editedCustomer) {
                editedCustomer.expanded = true;
                this.cdr.detectChanges();
              }
            }
          }, 100);
        }
      }
    });
  }

  onEditCompany(): void {
    if (!this.company) {
      console.error('Nincs cég adat a szerkesztéshez');
      return;
    }

    // Konvertáljuk a company adatokat CustomerListItem formátumra
    const customerData: CustomerListItem = {
      id: this.company.id,
      companyName: this.company.companyname || '',
      registrationNumber: this.company.registrationNumber || '',
      taxNumber: this.company.taxNumber || '',
      address: this.company.address || '',
      mainActivity: this.company.mainActivity || '',
      county: this.company.county || '',
      officials: [], // A customers-details-ben nincs officials info
      emails: this.company.emails?.map(e => e.email) || [],
      phoneNumbers: this.company.phones?.map(p => p.phone) || [],
      status: this.company.ccStatus || '',
      createdAt: this.company.createdat || '',
      type: 'company'
    };

    const dialogRef = this.dialog.open(NewCustomerDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'crm-dialog',
      data: {
        editMode: true,
        customerData: customerData,
        companyData: this.company // Teljes InsuranceCompany adat átadása
      }
    });

    dialogRef.afterClosed().subscribe((result: NewCustomerDialogResult | undefined) => {
      if (result && result.action === 'save') {
        
        // A dialog már mutatott success üzenetet, itt csak újratöltjük az adatokat
        // Újratöltjük az adatokat a megfelelő típus szerint
        if (this.company && this.company.id) {
          this.loadCompanyData(this.company.id.toString());
        } else if (this.customerId) {
          this.loadCustomerData(this.customerId);
        }
      }
    });
  }

  // Customer selection for adding contacts
  // selectedCustomerForPhone: any = null; // Removed duplicate

  setSelectedCustomer(customer: any): void {
    this.selectedCustomerForPhone = customer;
  }

  onCustomerPhoneAdded(phone: string): void {
    if (!this.selectedCustomerForPhone) {
      console.error('❌ Nincs kiválasztott ügyfél a telefonszám hozzáadáshoz');
      return;
    }

    if (!phone || !phone.trim()) {
      console.error('❌ Üres telefonszám');
      this.managementService.showError('Telefonszám megadása kötelező');
      return;
    }

    const customerId = this.selectedCustomerForPhone.customerId;
    const customerName = this.selectedCustomerForPhone.fullName;
    const expandedCustomerId = customerId;

    // Use contact management service
    this.contactService.addCustomerPhone(customerId, phone).subscribe({
      next: (response) => {
        if (response.success) {
          this.managementService.showSuccess(`Telefonszám "${phone}" sikeresen hozzáadva ${customerName} ügyfélhez!`);
          
          if (this.company?.id) {
            this.loadCompanyData(this.company.id.toString());
            setTimeout(() => {
              this.restoreCustomerExpansion(expandedCustomerId);
            }, 100);
          }
        } else {
          this.managementService.showError('Hiba a telefonszám hozzáadásakor');
        }
      },
      error: (error) => {
        console.error('❌ Hiba a telefonszám hozzáadásakor:', error);
        this.managementService.showError('Hiba történt a telefonszám hozzáadásakor');
      }
    });
    
    this.selectedCustomerForPhone = null;
  }

  onCustomerEmailAdded(email: string): void {
    if (!this.selectedCustomerForPhone) {
      console.error('❌ Nincs kiválasztott ügyfél az email hozzáadáshoz');
      return;
    }

    if (!email || !email.trim()) {
      console.error('❌ Üres email cím');
      this.managementService.showError('Email cím megadása kötelező');
      return;
    }

    const customerId = this.selectedCustomerForPhone.customerId;
    const customerName = this.selectedCustomerForPhone.fullName;
    const expandedCustomerId = customerId;

    // Use contact management service
    this.contactService.addCustomerEmail(customerId, email).subscribe({
      next: (response) => {
        if (response.success) {
          this.managementService.showSuccess(`Email cím "${email}" sikeresen hozzáadva ${customerName} ügyfélhez!`);
          
          if (this.company?.id) {
            this.loadCompanyData(this.company.id.toString());
            setTimeout(() => {
              this.restoreCustomerExpansion(expandedCustomerId);
            }, 100);
          }
        } else {
          this.managementService.showError('Hiba az email cím hozzáadásakor');
        }
      },
      error: (error) => {
        console.error('❌ Hiba az email cím hozzáadásakor:', error);
        this.managementService.showError('Hiba történt az email cím hozzáadásakor');
      }
    });
    
    this.selectedCustomerForPhone = null;
  }

  private restoreCustomerExpansion(customerId: number): void {
    if (this.company?.relatedCustomers) {
      const targetCustomer = this.company.relatedCustomers.find(c => c.id === customerId);
      if (targetCustomer) {
        targetCustomer.expanded = true;
        this.cdr.detectChanges();
      }
    }
  }

  onAddPhoneToCustomer(customer: any): void {
    // Implementation for adding phone to customer - development in progress
    this.managementService.showSuccess(`Telefonszám hozzáadása ${customer.fullName} ügyfélhez - fejlesztés alatt`);
  }

  // Chip menu functionality using new services
  get customMenuActions(): SmallIconMenuAction[] {
    return this.chipMenuService.customMenuActions;
  }

  // Chip click handler for email/phone chips
  onChipClick(chipText: string, trigger: MatMenuTrigger, chipType: 'email' | 'phone' = 'email', context: 'company' | 'customer' = 'company', customerId?: number): void {
    this.chipMenuState = this.chipMenuService.handleChipClick(chipText, trigger, chipType, context, customerId);
  }

  // Icon menu action handler
  async onIconMenuAction(action: SmallIconMenuAction): Promise<void> {
    await this.chipMenuService.handleIconMenuAction(
      action,
      this.chipMenuState,
      this.company,
      this.individualCustomer,
      (updates) => this.updateChipMenuState(updates),
      () => this.refreshInteractions()
    );
  }

  // Handle contact edit save
  async onContactEditSaved(newValue: string): Promise<void> {
    const success = await this.chipMenuService.handleContactEditSave(
      newValue,
      this.chipMenuState,
      this.company,
      this.individualCustomer,
      (updates) => this.updateChipMenuState(updates)
    );

    if (success) {
      // Trigger position calculation for edit menu
      this.calculateEditMenuPosition();
    }
  }

  // Handle contact edit cancel
  onContactEditCancelled(): void {
    this.chipMenuService.handleContactEditCancel(
      (updates) => this.updateChipMenuState(updates)
    );
  }

  private updateChipMenuState(updates: Partial<ChipMenuState>): void {
    this.chipMenuState = { ...this.chipMenuState, ...updates };
    this.cdr.detectChanges();
  }

  private calculateEditMenuPosition(): void {
    // This handles the position calculation that was moved out of the service
    if (this.chipMenuState.currentMenuTrigger && (this.chipMenuState.currentMenuTrigger as any)._element) {
      const rect = (this.chipMenuState.currentMenuTrigger as any)._element.nativeElement.getBoundingClientRect();
      this.updateChipMenuState({
        editTriggerX: rect.left + (rect.width / 2),
        editTriggerY: rect.top + (rect.height / 2)
      });

      // Open edit menu after position calculation
      setTimeout(() => {
        if (this.editTrigger && this.editInputMenuComponent?.menu) {
          this.editTrigger.openMenu();
          
          setTimeout(() => {
            if (this.editInputMenuComponent?.inputField) {
              this.editInputMenuComponent.inputField.nativeElement.focus();
              this.editInputMenuComponent.inputField.nativeElement.select();
            }
          }, 150);
        }
      }, 100);
    }
  }

  private refreshInteractions(): void {
    // Trigger change detection for chip updates
    this.cdr.detectChanges();
    
    setTimeout(() => {
      if (this.company?.id) {
        this.loadInteractionsForCompany(this.company.id);
      } else if (this.individualCustomer?.id) {
        this.loadInteractionsForCustomer(this.individualCustomer.id);
      }
    }, 200);
  }

  // Template accessor methods for chip menu state
  get selectedChipText(): string {
    return this.chipMenuState.selectedChipText;
  }

  get selectedChipType(): 'email' | 'phone' {
    return this.chipMenuState.selectedChipType;
  }

  get selectedChipContext(): 'company' | 'customer' {
    return this.chipMenuState.selectedChipContext;
  }

  get selectedCustomerId(): number | null {
    return this.chipMenuState.selectedCustomerId;
  }

  get editingContactValue(): string {
    return this.chipMenuState.editingContactValue;
  }

  get editTriggerX(): number {
    return this.chipMenuState.editTriggerX;
  }

  get editTriggerY(): number {
    return this.chipMenuState.editTriggerY;
  }

  get isUpdatingContact(): boolean {
    return this.chipMenuState.isUpdatingContact;
  }
  /**
   * Contracts loading for entity
   */
  private loadContractsForEntity(entityType: 'company' | 'customer', entityId: number): void {
    this.contractsLoading = true;
    this.contractsError = null;

    this.dataLoadingService.loadContractsForEntity(entityType, entityId).subscribe({
      next: (result) => {
        if (result.state.error) {
          this.contractsError = result.state.error;
        } else {
          this.contracts = result.data || [];
        }
        this.contractsLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading contracts:', error);
        this.contractsError = 'Hiba a szerződések betöltése során';
        this.contractsLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Interactions loading for company
   */
  private loadInteractionsForCompany(companyId: number): void {
    this.interactionsLoading = true;
    this.interactionsError = null;

    this.dataLoadingService.loadCompanyInteractions(companyId).subscribe({
      next: (result) => {
        if (result.state.error) {
          this.interactionsError = result.state.error;
        } else {
          this.interactions = result.data || [];
        }
        this.interactionsLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading interactions:', error);
        this.interactionsError = 'Hiba a logok betöltése során';
        this.interactionsLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Interactions loading for customer
   */
  private loadInteractionsForCustomer(customerId: number): void {
    this.interactionsLoading = true;
    this.interactionsError = null;

    this.dataLoadingService.loadCustomerInteractions(customerId).subscribe({
      next: (result) => {
        if (result.state.error) {
          this.interactionsError = result.state.error;
        } else {
          this.interactions = result.data || [];
        }
        this.interactionsLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading customer interactions:', error);
        this.interactionsError = 'Hiba a logok betöltése során';
        this.interactionsLoading = false;
        this.cdr.detectChanges();
      }
    });
  }
}